// Panels a slash command opens above the composer (D36): `/help` (the commands), `/mcp` (the organization's active
// integrations and their tools) and short notes. Read-only; closing returns focus to the field (the parent's onClose).
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { isApiError } from "../../api/client.ts";
import { listModuleInstances } from "../../api/endpoints-modules.ts";
import { listIntegrations } from "../../api/endpoints-wave3.ts";
import { useI18n } from "../../i18n/index.tsx";
import { IconButton } from "../../components/Button.tsx";
import { XIcon } from "../../components/icons.tsx";
import type { SlashCommand } from "./slash.ts";

export interface IntegrationHint {
  id: string;
  name: string;
  /** The integration kind (github_app, sentry…) or, for a module instance, its card (the tools it offers). */
  detail: string;
  source: "integration" | "module";
}

export type IntegrationHints = { state: "loading" } | { state: "ready"; items: IntegrationHint[] } | { state: "forbidden" } | { state: "failed" };

/** Active integrations and module instances of the organization (maintainer+ may list them; others get `forbidden`). */
export async function loadIntegrationHints(signal?: AbortSignal): Promise<IntegrationHints> {
  try {
    const [integrations, modules] = await Promise.all([listIntegrations(signal), listModuleInstances(signal)]);
    const items: IntegrationHint[] = [
      ...integrations.items.filter((i) => i.state === "active").map((i) => ({ id: i.id, name: i.name, detail: i.kind, source: "integration" as const })),
      ...modules.items.filter((m) => m.state === "active").map((m) => ({ id: m.id, name: m.display_name, detail: m.card_id, source: "module" as const })),
    ];
    return { state: "ready", items };
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    return isApiError(e) && (e.status === 403 || e.status === 401) ? { state: "forbidden" } : { state: "failed" };
  }
}

export function useIntegrationHints(enabled: boolean): IntegrationHints {
  const [hints, setHints] = useState<IntegrationHints>({ state: "loading" });
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    loadIntegrationHints(controller.signal).then(setHints, () => undefined);
    return () => controller.abort();
  }, [enabled]);
  return hints;
}

function Panel({ title, onClose, children, testId }: { title: string; onClose: () => void; children: ReactNode; testId: string }) {
  const { t } = useI18n();
  return (
    <section className="cmp-panel" aria-label={title} data-testid={testId}>
      <header className="cmp-panel-head">
        <h3 className="cmp-panel-title">{title}</h3>
        <IconButton label={t("composer.panel.close")} onClick={onClose}>
          <XIcon size={14} />
        </IconButton>
      </header>
      {children}
    </section>
  );
}

export function HelpPanel({ commands, onClose }: { commands: readonly SlashCommand[]; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <Panel title={t("composer.help.title")} onClose={onClose} testId="composer-help">
      <dl className="cmp-help">
        {commands.map((c) => (
          <div key={c.name} className="cmp-help-row">
            <dt>/{c.name}</dt>
            <dd>{t(c.description)}</dd>
          </div>
        ))}
      </dl>
      <p className="subtle cmp-panel-note">{t("composer.help.unknown")}</p>
    </Panel>
  );
}

export function McpPanel({ onClose, load = loadIntegrationHints }: { onClose: () => void; load?: (signal?: AbortSignal) => Promise<IntegrationHints> }) {
  const { t } = useI18n();
  const [hints, setHints] = useState<IntegrationHints>({ state: "loading" });
  const run = useCallback((signal?: AbortSignal) => load(signal).then(setHints, () => undefined), [load]);
  useEffect(() => {
    const controller = new AbortController();
    void run(controller.signal);
    return () => controller.abort();
  }, [run]);
  return (
    <Panel title={t("composer.mcp.title")} onClose={onClose} testId="composer-mcp">
      {hints.state === "loading" ? <p className="subtle cmp-panel-note">{t("composer.mcp.loading")}</p> : null}
      {hints.state === "forbidden" ? <p className="subtle cmp-panel-note">{t("composer.mcp.forbidden")}</p> : null}
      {hints.state === "failed" ? <p className="subtle cmp-panel-note">{t("composer.mcp.failed")}</p> : null}
      {hints.state === "ready" && hints.items.length === 0 ? <p className="subtle cmp-panel-note">{t("composer.mcp.empty")}</p> : null}
      {hints.state === "ready" && hints.items.length > 0 ? (
        <ul className="cmp-mcp">
          {hints.items.map((i) => (
            <li key={i.id}>
              <span className="cmp-mcp-name">{i.name}</span> <span className="subtle">{t("composer.mcp.tools", { list: i.detail })}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Panel>
  );
}

export function NotePanel({ text, onClose }: { text: string; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <div className="cmp-panel cmp-panel--note" role="status" data-testid="composer-note">
      <p className="cmp-panel-note">{text}</p>
      <IconButton label={t("composer.panel.close")} onClick={onClose}>
        <XIcon size={14} />
      </IconButton>
    </div>
  );
}
